import { AppApi } from '@/customApiClients/appApiClient/Api';
import { Building2, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger
} from '@/components/ui/dropdown-menu';

interface Organization {
    id: string;
    name: string;
    alias?: string;
}

interface DMOrganizationSwitcherProps {
    enabled: boolean;
    currentOrgId: string | null;
    currentOrgValue?: string | null;
}

export default function DMOrganizationSwitcher({
    enabled,
    currentOrgId,
    currentOrgValue = null
}: DMOrganizationSwitcherProps) {
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);
    const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const appApiClient = useMemo(
        () =>
            new AppApi({
                baseUrl: window.location.origin,
                baseApiParams: { credentials: 'include' }
            }),
        []
    );

    useEffect(() => {
        if (!enabled || organizations.length > 0 || loadingOrgs) return;

        const fetchOrganizations = async () => {
            setLoadingOrgs(true);
            setError(null);

            try {
                const response = await appApiClient.auth.getUserOrganizations();
                if (response.ok && response.data?.organizations) {
                    setOrganizations(
                        response.data.organizations as Organization[]
                    );
                } else {
                    setError('Failed to load organizations');
                }
            } catch {
                setError('Failed to load organizations');
            } finally {
                setLoadingOrgs(false);
            }
        };

        fetchOrganizations();
    }, [appApiClient, enabled, loadingOrgs, organizations.length]);

    const resolvedCurrentOrgId = useMemo(() => {
        const resolveOrgIdFromKey = (key: string | null) => {
            if (!key) return null;

            const exactIdMatch = organizations.find((org) => org.id === key);
            if (exactIdMatch) return exactIdMatch.id;

            const textMatches = organizations.filter(
                (org) => org.alias === key || org.name === key
            );

            return textMatches.length === 1 ? textMatches[0].id : null;
        };

        return (
            resolveOrgIdFromKey(currentOrgId) ??
            resolveOrgIdFromKey(
                localStorage.getItem('selected-organization-id')
            ) ??
            resolveOrgIdFromKey(currentOrgValue)
        );
    }, [currentOrgId, currentOrgValue, organizations]);

    const isCurrentOrganization = (org: Organization) => {
        if (!resolvedCurrentOrgId) return false;
        return resolvedCurrentOrgId === org.id;
    };

    const handleSwitchOrganization = async (org: Organization) => {
        if (switchingOrgId || isCurrentOrganization(org)) return;

        setSwitchingOrgId(org.id);
        setError(null);

        try {
            const response = await appApiClient.auth.selectOrganization({
                organizationId: org.id,
                organizationName: org.name,
                organizationAlias: org.alias || null
            });

            if (!response.ok || !response.data?.success) {
                throw new Error();
            }

            localStorage.removeItem('org-avatar-light');
            localStorage.removeItem('org-avatar-dark');
            localStorage.removeItem('org-font');
            localStorage.removeItem('org-font-size');
            localStorage.setItem('selected-organization-id', org.id);

            setTimeout(() => {
                window.location.href = '/';
            }, 500);
        } catch {
            setError('Failed to switch organization');
            setSwitchingOrgId(null);
        }
    };

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger>
                <Building2 />
                <span className="whitespace-nowrap">
                    Switch Organization
                </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="bg-background min-w-72 max-h-[70vh] overflow-y-auto">
                {loadingOrgs ? (
                    <DropdownMenuItem disabled>
                        <Loader2 className="animate-spin" />
                        <span>
                            Loading organizations...
                        </span>
                    </DropdownMenuItem>
                ) : organizations.length === 0 ? (
                    <DropdownMenuItem disabled>
                        <Building2 />
                        <span>
                            No organizations available
                        </span>
                    </DropdownMenuItem>
                ) : (
                    organizations
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((org) => (
                            <DropdownMenuItem
                                key={org.id}
                                onClick={() => handleSwitchOrganization(org)}
                                disabled={
                                    !!switchingOrgId ||
                                    isCurrentOrganization(org)
                                }
                               
                            >
                                <Building2
                                   
                                />
                                <div className="flex flex-col flex-1 min-w-0">
                                    <span className="text-foreground truncate">
                                        {org.name}
                                    </span>
                                </div>
                                {switchingOrgId === org.id ? (
                                    <Loader2 className="ml-auto animate-spin" />
                                ) : isCurrentOrganization(org) ? (
                                    <span className="ml-auto text-muted-foreground text-xs">
                                        (current)
                                    </span>
                                ) : null}
                            </DropdownMenuItem>
                        ))
                )}

                {error ? (
                    <DropdownMenuItem disabled>
                        <span className="text-destructive text-xs">
                            {error}
                        </span>
                    </DropdownMenuItem>
                ) : null}
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
}
